import { startRouter, currentRoute } from './router.js';
import { ensurePhase2Migration } from './storage/migrations.js';
import { loadState, onChange, refresh } from './core/state.js';
import { renderShell } from './components/app-shell.js';
import { requestPersistence } from './storage/persistence.js';
import { applyTheme } from './screens/settings-screen.js';
import './sync/index.js';
import { startNotificationLoop } from './settings/notifications.js';
import { captureInstallPrompt, isStandalone } from './settings/fullscreen.js';
import { toast } from './core/dom.js';
import { track, trackPageview, trackScreen, trackTiming, trackError, setUserProp, loadAnalytics } from './analytics/ga.js';
import { ensureAnalyticsConsent } from './analytics/consent.js';

const root = document.getElementById('app')!;

function renderShellSafe(): void {
  try {
    renderShell(root);
  } catch (err) {
    console.error('renderShell threw:', err);
    trackError('renderShell', err);
    root.innerHTML = `<pre style="padding:1rem;color:#f87171;white-space:pre-wrap;">Wean failed to render: ${String(err)}\n\nReload the page. If the problem persists, clear site data and try again.</pre>`;
  }
}

/** Friendly screen name from a hash path. Keeps cardinality bounded. */
function screenNameFor(path: string): string {
  // /plan/123 -> plan, /rescue?mod=x -> rescue
  const head = path.replace(/^\/+/, '').split(/[/?]/)[0] ?? '';
  return head || 'today';
}

function trackRoute(): void {
  const { path } = currentRoute();
  trackPageview(path);
  trackScreen(screenNameFor(path));
}

async function boot() {
  const bootStart = performance.now();

  // Global error capture — fires before app-level handlers so any thrown
  // error from any source is recorded.
  window.addEventListener('error', (e) => trackError('window.error', e.error ?? e.message));
  window.addEventListener('unhandledrejection', (e) => trackError('unhandledrejection', e.reason));

  await ensurePhase2Migration();
  const st = await loadState();
  applyTheme(st.settings.theme);

  // Pre-release analytics consent gate. Blocks until user agrees or is
  // redirected away. gtag.js is not loaded until after consent.
  const consented = await ensureAnalyticsConsent();
  if (!consented) return; // location.replace fired; abort boot.
  loadAnalytics();

  // User properties: low-cardinality enums only. No identifiers, no free text.
  setUserProp('app_theme', st.settings.theme);
  setUserProp('display_mode', isStandalone() ? 'standalone' : 'browser');
  setUserProp('reduced_motion', st.settings.reducedMotion);

  startRouter();
  renderShellSafe();
  trackRoute();
  // Fire-and-forget persistence request — non-blocking, no prompt UI noise.
  requestPersistence().catch(() => {});
  startNotificationLoop();
  captureInstallPrompt();

  // PWA install prompt lifecycle.
  window.addEventListener('beforeinstallprompt', () => track('install_prompt_available'));
  window.addEventListener('appinstalled', () => track('app_installed'));

  onChange(() => renderShellSafe());
  // Re-render shell on storage state change events
  window.addEventListener('wean:state-changed', async () => {
    await refresh();
  });
  // Pre-onboarding has no internal route subscription, so re-render the shell
  // on every hash change. Post-onboarding, app-shell sets up its own router
  // listener; this still re-renders harmlessly when state actually changes.
  window.addEventListener('hashchange', () => {
    renderShellSafe();
    trackRoute();
  });
  // In-app-only notification mode delivery: a calm bottom toast in lieu of OS.
  window.addEventListener('wean:notification', ((e: CustomEvent<{ title: string; body: string }>) => {
    const { title, body } = e.detail;
    toast(`${title}: ${body}`, 5000);
  }) as EventListener);
  // PWA service worker (only in production builds)
  if ('serviceWorker' in navigator && import.meta.env.PROD) {
    navigator.serviceWorker.register('./service-worker.js')
      .then(() => track('service_worker_registered'))
      .catch((err: unknown) => trackError('service_worker_register', err));
  }

  trackTiming('app_boot', performance.now() - bootStart, {
    standalone: isStandalone(),
  });
}

boot().catch((err) => {
  trackError('boot', err);
  root.innerHTML = `<pre style="padding:1rem;color:#f87171;">Failed to start: ${String(err)}</pre>`;
  console.error(err);
});

declare global {
  interface WindowEventMap {
    'wean:state-changed': CustomEvent;
    'wean:notification': CustomEvent<{ title: string; body: string; tag: string }>;
  }
}
